import type { ComposedForm } from 'lib-node-question-builder';
import { formIs, formIsNot } from 'lib-node-question-builder/utils';
import { useState } from 'react';

import { COUNTRY_LABEL_VALUE_ARRAY } from '~/common/constants/countries';
import { US_STATES_SHORT_OPTIONS_ARRAY } from '~/common/constants/us-states';
import useIsWriteAccessDisabled from '~/common/hooks/useIsWriteAccessDisabled';
import { AddFieldArrayRowButton } from '~/pages/Loan1003DataGrids/common/components/FormFieldArrayButtons';
import { READONLY_CONDITION } from '~/pages/Loan1003DataGrids/common/utils/utils';

import type { AddressInfoData } from '../../../types';
import { AddressCard, AddressCardContent } from './AddressCard';

type MailingAddressProps = {
  form: ComposedForm<AddressInfoData['formData']>;
  defaultWholeAddressString: string;
};

export function MailingAddress({
  form,
  defaultWholeAddressString,
}: Readonly<MailingAddressProps>) {
  const isWriteAccessDisabled = useIsWriteAccessDisabled();
  const [forceShowMailingFields, setForceShowMailingFields] = useState(false);

  const renderMailingAddressFields = () => {
    return (
      <>
        <form.Checkbox
          name="residenceHistoryDetails.mailingAddress.sameAsPrimary"
          label="Same as primary"
          hasBottomBorder={false}
          conditions={{
            isDisabled: READONLY_CONDITION,
          }}
        />
        <form.Grid name="mailingAddressGrid" columns={4}>
          <form.GridItem colSpan={3}>
            <form.Text
              name="residenceHistoryDetails.mailingAddress.addressLine1"
              label="Address Line 1"
              placeholder="Address Line 1"
              rules={{
                required: true,
              }}
              conditions={{
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem>
            <form.Text
              name="residenceHistoryDetails.mailingAddress.unit"
              label="Unit #"
              placeholder="Unit #"
              conditions={{
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem colSpan={2}>
            <form.Text
              name="residenceHistoryDetails.mailingAddress.city"
              label="City"
              placeholder="City"
              rules={{
                required: true,
              }}
              conditions={{
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem key="mailingAddress.state.us">
            <form.Select
              name="residenceHistoryDetails.mailingAddress.state"
              label="State"
              placeholder="Select State"
              options={US_STATES_SHORT_OPTIONS_ARRAY}
              rules={{
                required: true,
              }}
              conditions={{
                isVisible: formIs(
                  'residenceHistoryDetails.mailingAddress.country',
                  'US',
                ),
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem key="mailingAddress.state.nonUs">
            <form.Text
              name="residenceHistoryDetails.mailingAddress.state"
              label="State"
              placeholder="State/Province/Region"
              rules={{
                required: true,
              }}
              conditions={{
                isVisible: formIsNot(
                  'residenceHistoryDetails.mailingAddress.country',
                  'US',
                ),
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem key="mailingAddress.zipCode.us">
            <form.Text
              name="residenceHistoryDetails.mailingAddress.zipCode"
              label="Zip Code"
              placeholder="Zip Code"
              rules={{
                required: true,
              }}
              conditions={{
                isVisible: formIs(
                  'residenceHistoryDetails.mailingAddress.country',
                  'US',
                ),
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem key="mailingAddress.zipCode.nonUs">
            <form.Text
              name="residenceHistoryDetails.mailingAddress.zipCode"
              label="Postal Code"
              placeholder="Postal Code"
              rules={{
                required: true,
              }}
              conditions={{
                isVisible: formIsNot(
                  'residenceHistoryDetails.mailingAddress.country',
                  'US',
                ),
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
          <form.GridItem colSpan={2}>
            <form.Select
              name="residenceHistoryDetails.mailingAddress.country"
              label="Country"
              placeholder="Select Country"
              options={COUNTRY_LABEL_VALUE_ARRAY}
              rules={{
                required: true,
              }}
              conditions={{
                isDisabled: READONLY_CONDITION,
              }}
            />
          </form.GridItem>
        </form.Grid>
      </>
    );
  };

  return (
    <AddressCard>
      <AddressCardContent width="60%">
        <form.Subscribe
          selector={(state) => {
            return {
              sameAsPrimary:
                state.values?.residenceHistoryDetails?.mailingAddress
                  ?.sameAsPrimary === true,
            };
          }}
        >
          {({ sameAsPrimary }) => {
            const showAddAddressButton =
              defaultWholeAddressString.trim() === '' &&
              !forceShowMailingFields &&
              !sameAsPrimary;

            if (showAddAddressButton) {
              return (
                <AddFieldArrayRowButton
                  readOnlyMode={isWriteAccessDisabled}
                  onClick={() => {
                    setForceShowMailingFields(true);
                  }}
                >
                  Mailing Address
                </AddFieldArrayRowButton>
              );
            }

            return renderMailingAddressFields();
          }}
        </form.Subscribe>
      </AddressCardContent>
    </AddressCard>
  );
}
